import React from 'react';

import createZoomControl from './custom_zoom_control';
import { TerraDraw, TerraDrawPolygonMode, TerraDrawRectangleMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import type { HexColorStyling } from 'terra-draw/dist/common';

function addBtnClass(element, selected) {
  if (selected) {
    element.classList.add('btn-shaded-primary', 'text-primary');
  } else {
    element.classList.add('btn-light', 'bg-white', 'text-primary');
  }
}

const DRAW_STYLES: { outlineColor: HexColorStyling; outlineWidth: number; fillOpacity: number } = {
  fillOpacity: 0,
  outlineColor: '#7422CE',
  outlineWidth: 3
};

type CustomMapControlsProps = {
  map: google.maps.Map | null;
  position: number;
  drawingMode: string | null;
  onClear: () => void;
  onDrawComplete: (drawing: any) => void;
  setDrawingMode: (mode: string | null) => void;
};

export default function CustomMapControls({
  map,
  position,
  drawingMode,
  onClear,
  onDrawComplete,
  setDrawingMode
}: CustomMapControlsProps) {
  const [draw, setDraw] = React.useState<TerraDraw | null>(null);

  const handleModeChange = (mode) => {
    if (!draw) return;

    setDrawingMode(mode);
    if (draw.getSnapshot().length > 0) {
      draw.clear();
    }
    draw.start();
    draw.setMode(mode || 'polygon');
  };

  React.useEffect(() => {
    if (!map) return;

    const terraDraw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: google.maps,
        map,
        coordinatePrecision: 9,
      }),
      modes: [
        new TerraDrawPolygonMode({
          styles: { ...DRAW_STYLES, closingPointColor: '#1D357E' }
        }),
        new TerraDrawRectangleMode({ styles: DRAW_STYLES }),
        new TerraDrawSelectMode()
      ],
    });
    setDraw(terraDraw);

    return () => {
      // Clean up TerraDraw instance when component unmounts or map changes
      if (terraDraw) {
        terraDraw.stop();
      }
    };
  }, [map]);


  React.useEffect(() => {
    if (!draw) return;

    const onFinish = async (id, context) => {
      if (context.action === 'draw') {
        const currDrawing = await draw.getSnapshot().find(f => f.id === id);
        onDrawComplete(currDrawing);
        setDrawingMode(null);
        draw.setMode('select');
        draw.clear();
        draw.stop();
      }
    };

    draw.on('finish', onFinish);

    return () => {
      draw.off('finish', onFinish);
    };
  }, [draw, onDrawComplete, setDrawingMode]);

  React.useEffect(() => {
    if (!map) return;
    const controls = map.controls[position];
    controls.clear();

    const container = document.createElement('div');
    container.classList.add('p-1', 'bg-white', 'rounded', 'm-2', 'map-contols', 'shadow');

    const btnGroup = document.createElement('div');
    btnGroup.classList.add('btn-group', 'gap-1');

    const dragDiv = document.createElement('div');
    dragDiv.innerHTML = '<i class="fa-regular fa-hand" />';
    dragDiv.classList.add('btn', 'border-0');
    addBtnClass(dragDiv, !drawingMode);
    dragDiv.onclick = function () {
      setDrawingMode(null);
      if (draw?.enabled) {
        draw.setMode('select');
      }
    };

    const polygonDiv = document.createElement('div');
    polygonDiv.innerHTML = '<i class="fa-regular fa-draw-polygon" />';
    polygonDiv.classList.add('btn', 'border-0');
    addBtnClass(polygonDiv, drawingMode === 'polygon');
    polygonDiv.onclick = function () {
      handleModeChange('polygon');
    };

    const rectangleDiv = document.createElement('div');
    rectangleDiv.innerHTML = '<i class="fa-regular fa-draw-square" />';
    rectangleDiv.classList.add('btn', 'border-0');
    addBtnClass(rectangleDiv, drawingMode === 'rectangle');
    rectangleDiv.onclick = function () {
      handleModeChange('rectangle');
    };

    const clearDiv = document.createElement('div');
    clearDiv.innerHTML = '<i class="fa fas fa-times text-danger -sm" />';
    clearDiv.classList.add('btn', 'btn-light', 'bg-white', 'border-0');
    clearDiv.onclick = function () {
      setDrawingMode(null);
      onClear();
      if (draw?.enabled) {
        draw.setMode('select');
      }
    };

    btnGroup.append(dragDiv);
    btnGroup.append(polygonDiv);
    btnGroup.append(rectangleDiv);
    btnGroup.append(clearDiv);
    container.append(btnGroup);

    controls.push(container);

    if (map.controls[google.maps.ControlPosition.RIGHT_CENTER].getLength() < 1) {
      createZoomControl(map);
    }

    const index = controls.getLength();
    return () => {
      controls.removeAt(index - 1);
    };
  }, [map, drawingMode, draw]);

  return null;
}
